import "./DrillProgress.css";

export interface DrillProgressProps {
  /** 1始まりの現在の問番 */
  readonly current: number;
  /** レッスン（または試験）内の総問数 */
  readonly total: number;
}

/** 点表示に切り替える上限。これを超えるレッスンは軌跡表示にする（点が潰れて読めなくなるため）。 */
const DOT_DISPLAY_LIMIT = 12;

/**
 * レッスン内の進み具合（screen-flow.md §7.2 Z3「稽古ドット」）。
 * 「終わりが見えない」を防ぐため、問番・残り・視覚的な進みを常時出す。
 */
export function DrillProgress({ current, total }: DrillProgressProps) {
  const safeTotal = Math.max(total, 1);
  const safeCurrent = Math.min(Math.max(current, 1), safeTotal);
  const remaining = safeTotal - safeCurrent;
  const useDots = safeTotal <= DOT_DISPLAY_LIMIT;
  const fillRatio = safeCurrent / safeTotal;

  return (
    <div
      className="drill-progress"
      role="status"
      aria-label={`問い ${safeCurrent} / ${safeTotal}、残り ${remaining}問`}
    >
      <div className="drill-progress__meta">
        <span className="drill-progress__count">
          問い {safeCurrent} / {safeTotal}
        </span>
        <span className="drill-progress__remaining">残り {remaining}問</span>
      </div>
      {useDots ? (
        <div className="drill-progress__dots" aria-hidden="true">
          {Array.from({ length: safeTotal }, (_, index) => {
            const position = index + 1;
            const className = [
              "drill-progress__dot",
              position < safeCurrent ? "drill-progress__dot--done" : null,
              position === safeCurrent ? "drill-progress__dot--current" : null,
            ]
              .filter(Boolean)
              .join(" ");
            return <span key={position} className={className} />;
          })}
        </div>
      ) : (
        <div className="drill-progress__track" aria-hidden="true">
          <span
            className="drill-progress__track-fill"
            style={{ width: `${Math.round(fillRatio * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
